import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchWeeklyCosts, saveWeeklyCosts, type WeekForecast } from '../../api/weeklyCosts'
import { formatMoneyInputBlur, formatMoney } from '../../lib/formatters'
import { useSpeechDictation } from '../../hooks/useSpeechDictation'
import AnswerMicButton from '../../screens/projects/modal/costsMargin/AnswerMicButton'
import { useActionToast } from '../../lib/useActionToast'
import { actionErrorMessage } from '../../lib/actionErrorMessage'
import PrefixedMoneyInput, { type PrefixedMoneyInputHandle } from '../../components/PrefixedMoneyInput'
import SorenWeekVerdictCard from './SorenWeekVerdictCard'
import {
  amountsFromMergedValues,
  mergeLiveDraftsIntoValues,
  totalFromValuesAndDrafts,
} from './sorenCostsFormValues.js'

type CostField = 'payroll' | 'subs' | 'materials' | 'other'
import '../../screens/projects/modal/costsMargin/costsMargin.css'

const FIELDS: { key: CostField; label: string }[] = [
  { key: 'payroll', label: 'Payroll' },
  { key: 'subs', label: 'Subs' },
  { key: 'materials', label: 'Materials' },
  { key: 'other', label: 'Other' },
]

function rowAmount(row: { payroll?: number; subs?: number; materials?: number; other?: number } | null | undefined, key: CostField) {
  if (!row) return ''
  const n = Number(row[key])
  if (!Number.isFinite(n) || n <= 0) return ''
  return formatMoneyInputBlur(String(n))
}

function valuesFromRow(row: { payroll?: number; subs?: number; materials?: number; other?: number } | null | undefined) {
  return {
    payroll: rowAmount(row, 'payroll'),
    subs: rowAmount(row, 'subs'),
    materials: rowAmount(row, 'materials'),
    other: rowAmount(row, 'other'),
  }
}

export default function SorenCostsForm({ weekStart }: { weekStart?: string | null }) {
  const queryClient = useQueryClient()
  const { showToast, Toast } = useActionToast()
  const [values, setValues] = useState<Record<CostField, string>>({
    payroll: '', subs: '', materials: '', other: '',
  })
  const [activeField, setActiveField] = useState<CostField>('payroll')
  const [busy, setBusy] = useState(false)
  const [mode, setMode] = useState<'form' | 'verdict'>('form')
  const [forecast, setForecast] = useState<WeekForecast | null>(null)
  const [liveDrafts, setLiveDrafts] = useState<Partial<Record<CostField, string>>>({})
  const editingRef = useRef(false)
  const fieldRefs = useRef<Record<CostField, PrefixedMoneyInputHandle | null>>({
    payroll: null,
    subs: null,
    materials: null,
    other: null,
  })

  const costsQ = useQuery({
    queryKey: ['weekly-costs', weekStart || 'current'],
    queryFn: () => fetchWeeklyCosts(weekStart || undefined),
  })

  useEffect(() => {
    const d = costsQ.data
    if (!d) return
    if (editingRef.current) return
    if (d.current && d.forecast) {
      setForecast(d.forecast)
      setMode('verdict')
      return
    }
    const src = d.current || d.prefill
    setValues(valuesFromRow(src))
    if (!d.current) {
      setMode('form')
      setForecast(null)
    }
  }, [costsQ.data])

  const speech = useSpeechDictation({
    onFinalText: (text) => {
      setValues((v) => ({ ...v, [activeField]: text }))
    },
    onInterim: () => {},
    onListeningChange: () => {},
  })

  function focusField(key: CostField) {
    const el = fieldRefs.current[key]
    if (el) el.focus()
  }

  function focusNextField(from: CostField) {
    const idx = FIELDS.findIndex((f) => f.key === from)
    const next = FIELDS[idx + 1]
    if (next) focusField(next.key)
  }

  function commitAllFields(): Record<CostField, string> {
    const next = { ...values }
    for (const f of FIELDS) {
      const el = fieldRefs.current[f.key]
      if (el?.commitDraft) {
        next[f.key] = el.commitDraft()
      }
    }
    const merged = mergeLiveDraftsIntoValues(next, liveDrafts)
    setValues(merged)
    setLiveDrafts({})
    return merged
  }

  async function handleSave(source: 'typed' | 'voice' = 'typed', merged?: Record<CostField, string>) {
    setBusy(true)
    try {
      const body = amountsFromMergedValues(merged || commitAllFields())
      const saved = await saveWeeklyCosts({
        week_start: costsQ.data?.week_start || weekStart || undefined,
        ...body,
        source,
      })
      editingRef.current = false
      await queryClient.invalidateQueries({ queryKey: ['weekly-costs'] })
      if (saved.forecast) {
        setForecast(saved.forecast)
        setMode('verdict')
      } else {
        showToast('Weekly costs saved')
      }
    } catch (e) {
      showToast(actionErrorMessage(e, 'Could not save costs'))
    } finally {
      setBusy(false)
    }
  }

  const total = totalFromValuesAndDrafts(values, liveDrafts)

  if (mode === 'verdict' && forecast) {
    return (
      <div className="soren-costs-form" data-testid="soren-costs-verdict">
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>This week&apos;s cash</h3>
        <SorenWeekVerdictCard
          forecast={forecast}
          onEditCosts={() => {
            void (async () => {
              editingRef.current = true
              setMode('form')
              const fresh = await queryClient.fetchQuery({
                queryKey: ['weekly-costs', weekStart || 'current'],
                queryFn: () => fetchWeeklyCosts(weekStart || undefined),
              })
              setValues(valuesFromRow(fresh.current))
              if (fresh.forecast) setForecast(fresh.forecast)
            })()
          }}
        />
        {Toast}
      </div>
    )
  }

  return (
    <div className="soren-costs-form" data-testid="soren-costs-form">
      <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>This week&apos;s costs</h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--ink4)' }} data-testid="soren-costs-total">
        Pre-filled from last week when empty. Total going out: <strong>{formatMoney(total)}</strong>
      </p>
      {FIELDS.map((f) => (
        <label key={f.key} style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--ink4)' }}>{f.label}</span>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <PrefixedMoneyInput
              ref={(el) => { fieldRefs.current[f.key] = el }}
              value={values[f.key]}
              onFocus={() => setActiveField(f.key)}
              onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
              onDraftChange={(draft) => {
                setLiveDrafts((prev) => {
                  if (!draft) {
                    const next = { ...prev }
                    delete next[f.key]
                    return next
                  }
                  return { ...prev, [f.key]: draft }
                })
              }}
              onEnterKey={() => {
                const idx = FIELDS.findIndex((x) => x.key === f.key)
                const el = fieldRefs.current[f.key]
                const committed = el?.commitDraft?.() ?? values[f.key]
                const nextValues = { ...values, [f.key]: committed }
                const nextDrafts = { ...liveDrafts }
                delete nextDrafts[f.key]
                if (idx < FIELDS.length - 1) {
                  setValues(nextValues)
                  setLiveDrafts(nextDrafts)
                  focusNextField(f.key)
                } else {
                  const merged = mergeLiveDraftsIntoValues(nextValues, nextDrafts)
                  setValues(merged)
                  setLiveDrafts({})
                  void handleSave(speech.listening ? 'voice' : 'typed', merged)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && !e.shiftKey) {
                  const idx = FIELDS.findIndex((x) => x.key === f.key)
                  if (idx < FIELDS.length - 1) {
                    e.preventDefault()
                    focusNextField(f.key)
                  }
                }
              }}
              data-testid={`soren-costs-${f.key}`}
            />
            <AnswerMicButton
              listening={speech.listening && activeField === f.key}
              disabled={busy}
              onToggle={() => {
                setActiveField(f.key)
                speech.toggle(values[f.key])
              }}
            />
          </div>
        </label>
      ))}
      <button
        type="button"
        className="btn btn-primary"
        disabled={busy || costsQ.isPending}
        onClick={() => void handleSave(speech.listening ? 'voice' : 'typed')}
        data-testid="soren-costs-save"
        style={{ marginTop: 8, width: '100%' }}
      >
        Save costs
      </button>
      {Toast}
    </div>
  )
}
